/**
 * useTransferProcessingState hook
 * WP-TRANSFER-1: Tracks when transfer detection is in progress
 *
 * This hook provides a global state for tracking transfer detection operations.
 * Components can subscribe to this state to show processing indicators.
 *
 * The state is managed via a simple pub/sub pattern to avoid prop drilling.
 */
import { useState, useEffect, useCallback } from 'react';

// Simple global state for transfer processing
// Using module-level state to avoid context overhead
let isProcessing = false;
const listeners = new Set<(processing: boolean) => void>();

/**
 * Update the global processing state and notify all listeners
 */
export function setTransferProcessing(processing: boolean): void {
  isProcessing = processing;
  listeners.forEach((listener) => listener(processing));
}

/**
 * Hook to subscribe to transfer processing state changes
 * @returns Current processing state and setter function
 */
export function useTransferProcessingState(): {
  isProcessing: boolean;
  setProcessing: (processing: boolean) => void;
} {
  const [processing, setLocalProcessing] = useState(isProcessing);

  useEffect(() => {
    // Subscribe to changes
    const listener = (newState: boolean) => {
      setLocalProcessing(newState);
    };
    listeners.add(listener);

    // Sync with current state on mount
    setLocalProcessing(isProcessing);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const setProcessing = useCallback((newState: boolean) => {
    setTransferProcessing(newState);
  }, []);

  return { isProcessing: processing, setProcessing };
}

/**
 * Hook for components that perform transfer operations
 * Provides a wrapper that sets processing state during async operations
 */
export function useTransferOperation(): {
  isProcessing: boolean;
  withProcessing: <T>(operation: () => Promise<T>) => Promise<T>;
} {
  const { isProcessing, setProcessing } = useTransferProcessingState();

  const withProcessing = useCallback(
    async <T>(operation: () => Promise<T>): Promise<T> => {
      setProcessing(true);
      try {
        return await operation();
      } finally {
        setProcessing(false);
      }
    },
    [setProcessing]
  );

  return { isProcessing, withProcessing };
}
